import axios from 'axios';
import { API_ROUTES } from '@/const/apiRoutes';
import { decodeJWT } from '@/utils/jwtDecoder';
import { PUBLIC_PATHS } from '@/const/routes';

// Ensure BASE_URL is defined in API_ROUTES or replace with the actual base URL string
const BASE_URL = API_ROUTES.BASE_URL

/** After logout, in-flight requests may 401; skip the hard /login redirect briefly so SPA can reach public `/`. */
let loginRedirectSuppressedUntil = 0

export function suppressAxiosLoginRedirectForMs(ms = 3000) {
  loginRedirectSuppressedUntil = Date.now() + ms
}

// Request queuing system to prevent multiple simultaneous token refresh requests
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Create an instance of axios with the base URL
const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // REQUIRED for cross-domain cookie support
});

// Request interceptor to add the Authorization header (backward compatibility)
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      // Maintain backward compatibility with Authorization header
      // Backend will use cookies if available, otherwise fall back to header
      config.headers['Authorization'] = `${token}`;
      // Don't set Content-Type for FormData — the browser must set it automatically
      // so it includes the multipart boundary (e.g. multipart/form-data; boundary=---xyz).
      // Forcing application/json here breaks file uploads.
      if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json';
      }
    }

    // Decode JWT and save the user ID to localStorage if not already saved
    if (token && !localStorage.getItem('user_id')) {
      const decodedToken = decodeJWT(token);
      if (decodedToken && decodedToken.id) {
        localStorage.setItem('user_id', decodedToken.id);
      } else {
        console.error('Failed to decode token or extract user ID');
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling token refresh
api.interceptors.response.use(
  (response) => response,
  // eslint-disable-next-line complexity
  async (error) => {
    const originalRequest = error.config;

    // Don't attempt token refresh for login or refresh endpoints
    const isLoginEndpoint = originalRequest.url?.includes('/user-login');
    const isRefreshEndpoint = originalRequest.url?.includes('/user-jwt-refresh');

    // Handle 401 errors with automatic token refresh
    if (error.response && error.response.status === 401 && !originalRequest._retry && !isLoginEndpoint && !isRefreshEndpoint) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (token && originalRequest.headers) {
              originalRequest.headers['Authorization'] = `${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh using cookie (preferred) or localStorage token
        const refreshToken = localStorage.getItem('refresh_token');
        const refreshPayload = refreshToken ? { jwt_refresh: refreshToken } : {};

        const { data } = await axios.post(
          API_ROUTES.USER_JWT_REFRESH,
          refreshPayload,
          { withCredentials: true } // Include cookies in refresh request
        );

        // Store new access token in localStorage as backup
        if (data.jwt_access) {
          localStorage.setItem('token', data.jwt_access);
        }

        // Store refresh token if provided
        if (data.jwt_refresh) {
          localStorage.setItem('refresh_token', data.jwt_refresh);
        }

        // Update original request Authorization header with new access token
        if (data.jwt_access && originalRequest.headers) {
          originalRequest.headers['Authorization'] = `${data.jwt_access}`;
        }

        // Decode the new token and save user ID to localStorage if not present
        if (data.jwt_access) {
          const decodedToken = decodeJWT(data.jwt_access);
          if (decodedToken && decodedToken.id) {
            localStorage.setItem('user_id', decodedToken.id);
          } else {
            console.error('Failed to decode new token or extract user ID');
          }
        }

        // Sync reactive role state in auth store with the new token
        // This is critical to ensure role-based UI updates after token refresh
        try {
          const { useAuthStore } = await import('@/stores/auth');
          const authStore = useAuthStore();
          authStore.updateRoleFromToken();
        } catch (err) {
          console.error('Failed to update role from refreshed token:', err);
        }

        // Process queued requests
        processQueue(null, data.jwt_access);
        isRefreshing = false;

        // Retry original request
        return api(originalRequest);
      } catch (refreshError: any) {
        // If refreshing the token fails, clear stored tokens and reject all queued requests
        processQueue(refreshError, null);
        isRefreshing = false;

        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_id');

        // Redirect to login if refresh fails (only in browser environment)
        if (typeof window !== 'undefined') {
          if (Date.now() < loginRedirectSuppressedUntil) {
            return Promise.reject(refreshError);
          }
          // Don't redirect if already on login page or if it's a public route
          // Use startsWith check so paths like /referral-action/:token also match
          const currentPath = window.location.pathname;
          const isPublicPath = PUBLIC_PATHS.some((p) => currentPath === p || currentPath.startsWith(p + '/'));
          if (!isPublicPath) {
            // Use setTimeout to avoid navigation during interceptor
            setTimeout(() => {
              window.location.href = '/login';
            }, 100);
          }
        }

        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default api;